import { useEffect, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const getRedirectUrl = (banner) => {
  if (!banner) {
    return '';
  }
  return (
    banner.redirectUrl ??
    banner.redirect_url ??
    banner.url ??
    banner.link ??
    ''
  );
};

const normalizeBanners = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return source
    .map((banner, index) => ({
      id:
        banner.bannerId ??
        banner.banner_id ??
        banner.id ??
        `banner-${index}`,
      image: banner.image,
      title: banner.title || banner.product?.name || '',
      subtitle: banner.description || banner.product?.description || '',
      priority: banner.priority ?? index,
      redirectUrl: getRedirectUrl(banner),
    }))
    .filter((banner) => Boolean(banner.image))
    .sort((a, b) => a.priority - b.priority);
};

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;

    Api.fetchBanners()
      .then((data) => {
        if (!mounted) return;

        const normalized = normalizeBanners(data);
     
        if (normalized.length) {
          setBanners(normalized);
          setActiveIndex(0);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setBanners([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!banners.length) return undefined;

    const intervalId = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % banners.length);
    }, 6000);

    return () => clearInterval(intervalId);
  }, [banners]);

  if (!banners.length) {
    return null;
  }

  return (
    <section className="banner" aria-label={t('banner.aria_label')}>
      <div className="banner__slides">
        {banners.map((banner, index) => {
          const backgroundImage = banner.image
            ? `url("${encodeURI(banner.image)}")`
            : undefined;
          const isClickable = Boolean(banner.redirectUrl);
          const handleClick = () => {
            if (!isClickable) return;
            if (typeof window !== 'undefined') {
              window.location.assign(banner.redirectUrl);
            }
          };
          const handleKeyPress = (event) => {
            if (!isClickable) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleClick();
            }
          };

          return (
            <div
              key={banner.id}
              className={`banner__slide ${
                index === activeIndex ? 'banner__slide--active' : ''
              }`}
              style={{ backgroundImage }}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? handleClick : undefined}
              onKeyPress={isClickable ? handleKeyPress : undefined}
            >
             
            </div>
          );
        })}
      </div>
      <div className="banner__dots">
        {banners.map((banner, index) => (
          <button
            key={`${banner.id}-dot`}
            type="button"
            className={`banner__dot ${
              index === activeIndex ? 'banner__dot--active' : ''
            }`}
            onClick={() => setActiveIndex(index)}
            aria-label={`${t('banner.dot_prefix')} ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default BannerCarousel;
